// Prediction Model v2 — statistický model všech signálů platformy + AI komentář.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crosshair, CaretDown, CaretUp, Sparkle, ArrowsClockwise } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import VoteButtons from "../components/VoteButtons";
import { ModelAccuracy } from "../components/ModelAccuracy";
import { getPredictionsV2, getPredictionAiSummary, fetchVotesBulk } from "../lib/api";
import { track } from "../lib/analytics";

const WINDOWS = [
    { value: 1, label: "Příští GW" },
    { value: 4, label: "Blok 4 GW" },
    { value: 6, label: "Blok 6 GW" },
];
const POSITIONS = ["GKP", "DEF", "MID", "FWD"];
const CONF = {
    HIGH: { label: "Vysoká", cls: "bg-fa-mint text-black" },
    MEDIUM: { label: "Střední", cls: "bg-fa-yellow text-black" },
    LOW: { label: "Nízká", cls: "border border-white/30 text-white/60" },
};
const FDR_COLOR = { 1: "#9EE8B4", 2: "#9DB7F5", 3: "#FFF3C2", 4: "#FF8800", 5: "#FF2A2A" };

function FloorCeil({ floor, total, ceiling }) {
    const max = Math.max(ceiling, 1);
    return (
        <div className="w-28">
            <div className="relative h-1.5 bg-white/10">
                <div className="absolute h-full bg-fa-sky/40" style={{ left: `${(floor / max) * 100}%`, width: `${((ceiling - floor) / max) * 100}%` }} />
                <div className="absolute h-full w-0.5 bg-fa-mint" style={{ left: `${(total / max) * 100}%` }} />
            </div>
            <div className="flex justify-between font-mono text-[8px] text-white/40 mt-0.5 tabular-nums">
                <span>{floor}</span><span>{ceiling}</span>
            </div>
        </div>
    );
}

function AiPanel({ window }) {
    const [doc, setDoc] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async (refresh = false) => {
        setBusy(true);
        try {
            const d = await getPredictionAiSummary(window, refresh);
            setDoc(d);
        } catch { if (refresh) toast.error("AI komentář se nepodařil"); }
        finally { setBusy(false); }
    }, [window]);

    useEffect(() => { setDoc(null); }, [window]);

    return (
        <div className="border border-fa-sky/30 bg-fa-sky/5 p-4" data-testid="pred-ai-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-mono text-[9px] uppercase tracking-widest text-fa-sky flex items-center gap-1.5">
                    <Sparkle size={11} weight="fill" /> AI komentář k predikcím
                </div>
                <button type="button" onClick={() => load(!!doc)} disabled={busy} data-testid="pred-ai-btn"
                        className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border border-fa-sky/60 text-fa-sky hover:bg-fa-sky hover:text-black disabled:opacity-40 flex items-center gap-1">
                    <ArrowsClockwise size={10} className={busy ? "animate-spin" : ""} />
                    {busy ? "Generuji…" : doc ? "Obnovit" : "Vygenerovat komentář"}
                </button>
            </div>
            {doc?.text && (
                <p className="font-mono text-[11px] text-white/75 mt-3 whitespace-pre-line leading-relaxed" data-testid="pred-ai-text">{doc.text}</p>
            )}
        </div>
    );
}

export default function Predictions() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [window, setWindow] = useState(1);
    const [position, setPosition] = useState("");
    const [priceMax, setPriceMax] = useState(16);
    const [expanded, setExpanded] = useState(null);
    const [votes, setVotes] = useState({});

    useEffect(() => { track("tool_open", { tool: "predictions_v2" }); }, []);

    useEffect(() => {
        setLoading(true);
        const h = setTimeout(() => {
            getPredictionsV2({ window, position: position || undefined, price_max: priceMax < 16 ? priceMax : undefined, top_n: 80 })
                .then((d) => {
                    setData(d);
                    const ids = (d.results || []).map((x) => x.fpl_id);
                    if (ids.length) fetchVotesBulk(ids, window).then((v) => setVotes(v.votes || v || {})).catch(() => {});
                })
                .catch(() => toast.error("Načtení predikcí selhalo"))
                .finally(() => setLoading(false));
        }, 300);
        return () => clearTimeout(h);
    }, [window, position, priceMax]);

    const rows = useMemo(() => data?.results || [], [data]);

    return (
        <AppShell>
            <div className="space-y-5" data-testid="predictions-page">
                <header className="border-2 border-fa-mint/40 bg-gradient-to-br from-fa-mint/10 via-transparent to-fa-sky/5 p-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint flex items-center gap-2">
                        <Crosshair size={11} weight="fill" /> Manager Lab · Select
                    </div>
                    <h1 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl mt-1">Prediction Model v2</h1>
                    <p className="font-mono text-[11px] text-white/65 mt-2 max-w-3xl">
                        Predikce bodů z kombinace všech signálů — zmrazené FDR profily, konzistence, výbušnost, haul rate, fáze sezóny, minuty, xG rozpisu a Radar u nováčků. Floor–strop ukazuje interval jistoty.
                    </p>
                </header>

                {/* Ovládání */}
                <div className="border border-white/10 bg-[#0d0d0d] p-4 flex flex-wrap items-center gap-4" data-testid="pred-controls">
                    <div className="flex gap-1">
                        {WINDOWS.map((w) => (
                            <button key={w.value} type="button" onClick={() => setWindow(w.value)}
                                    data-testid={`pred-window-${w.value}`}
                                    className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${window === w.value ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/60 hover:text-white"}`}>
                                {w.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-1">
                        <button type="button" onClick={() => setPosition("")} data-testid="pred-pos-all"
                                className={`font-mono text-[10px] px-2 py-1.5 border ${!position ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/60"}`}>VŠE</button>
                        {POSITIONS.map((p) => (
                            <button key={p} type="button" onClick={() => setPosition(p)} data-testid={`pred-pos-${p}`}
                                    className={`font-mono text-[10px] px-2 py-1.5 border ${position === p ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/60"}`}>{p}</button>
                        ))}
                    </div>
                    <label className="font-mono text-[9px] uppercase tracking-widest text-white/50 flex items-center gap-2">
                        Max cena £{priceMax}
                        <input type="range" min={4} max={16} step={0.5} value={priceMax}
                               onChange={(e) => setPriceMax(Number(e.target.value))}
                               data-testid="pred-price-max" className="w-32 accent-[#B8FFDD]" />
                    </label>
                    {data && (
                        <span className="font-mono text-[10px] text-white/45 ml-auto" data-testid="pred-meta">
                            GW {data.gw_from}{data.gw_to !== data.gw_from ? `–${data.gw_to}` : ""} · fáze {data.phase?.replace("gw", "GW ").replace("_", "–")} · {data.count_total} hráčů
                        </span>
                    )}
                </div>

                <AiPanel window={window} />

                <ModelAccuracy />

                {/* Tabulka */}
                <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="font-mono text-[9px] uppercase tracking-widest text-white/40 border-b border-white/10">
                                <th className="px-3 py-2">#</th>
                                <th className="px-3 py-2">Hráč</th>
                                <th className="px-3 py-2">Tým · Poz</th>
                                <th className="px-3 py-2 text-right">Cena</th>
                                <th className="px-3 py-2 text-right">Predikce</th>
                                <th className="px-3 py-2 text-right">/ GW</th>
                                <th className="px-3 py-2">Floor–Strop</th>
                                <th className="px-3 py-2">Jistota</th>
                                <th className="px-3 py-2 text-right">Vl. %</th>
                                <th className="px-3 py-2 text-center">Komunita</th>
                                <th className="px-3 py-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={11} className="px-3 py-8 text-center font-mono text-[11px] text-white/40">Počítám predikce…</td></tr>}
                            {!loading && rows.map((p, i) => (
                                <React.Fragment key={p.fpl_id}>
                                    <tr className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" data-testid={`pred-row-${i}`}
                                        onClick={() => setExpanded(expanded === p.fpl_id ? null : p.fpl_id)}>
                                        <td className="px-3 py-2 font-mono text-[10px] text-white/40">{i + 1}</td>
                                        <td className="px-3 py-2">
                                            <Link to={`/players/${p.fpl_id}`} onClick={(e) => e.stopPropagation()}
                                                  className="font-display font-black text-sm hover:text-fa-mint">{p.name}</Link>
                                            {p.is_radar && <span className="font-mono text-[8px] uppercase text-fa-sky border border-fa-sky/40 px-1 ml-1.5" title={`Radar readiness ${p.radar_readiness ?? "?"}`}>RADAR</span>}
                                            {p.new_coach && <span className="font-mono text-[8px] uppercase text-[#FF8800] border border-[#FF8800]/50 px-1 ml-1.5" title="Nový trenér — snížená jistota predikce">NT</span>}
                                        </td>
                                        <td className="px-3 py-2 font-mono text-[10px] text-white/55">{p.team} · {p.position}</td>
                                        <td className="px-3 py-2 text-right font-mono text-[11px] text-fa-yellow tabular-nums">£{p.price?.toFixed(1)}</td>
                                        <td className="px-3 py-2 text-right font-display font-black text-base text-fa-mint tabular-nums">{p.predicted_total}</td>
                                        <td className="px-3 py-2 text-right font-mono text-[11px] text-white/70 tabular-nums">{p.predicted_per_gw}</td>
                                        <td className="px-3 py-2"><FloorCeil floor={p.floor} total={p.predicted_total} ceiling={p.ceiling} /></td>
                                        <td className="px-3 py-2">
                                            <span className={`font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 ${CONF[p.confidence]?.cls || ""}`}>{CONF[p.confidence]?.label || p.confidence}</span>
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-[10px] text-white/55 tabular-nums">{p.ownership_pct}%</td>
                                        <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                                            <VoteButtons playerId={p.fpl_id} gwWindow={window} votes={votes[p.fpl_id]} />
                                        </td>
                                        <td className="px-3 py-2 text-right text-fa-sky">{expanded === p.fpl_id ? <CaretUp size={12} /> : <CaretDown size={12} />}</td>
                                    </tr>
                                    {expanded === p.fpl_id && (
                                        <tr className="border-b border-white/10 bg-black/30" data-testid={`pred-detail-${p.fpl_id}`}>
                                            <td colSpan={11} className="px-4 py-3">
                                                <div className="grid md:grid-cols-3 gap-4">
                                                    <div>
                                                        <p className="font-mono text-[8px] uppercase tracking-widest text-white/40 mb-1.5">Rozpis ({data.window} GW)</p>
                                                        <div className="flex gap-1.5 flex-wrap">
                                                            {p.fixtures.map((f, j) => f.opp ? (
                                                                <span key={j} className="font-mono text-[10px] px-2 py-1 border border-white/10"
                                                                      style={{ color: FDR_COLOR[Math.round(f.fdr)] || "#fff" }}>
                                                                    GW{f.gw} {f.opp}{f.home ? "(D)" : "(V)"} · FDR {f.fdr} · ×{f.mult} → {f.exp}b
                                                                </span>
                                                            ) : (
                                                                <span key={j} className="font-mono text-[10px] px-2 py-1 border border-white/10 text-white/30">GW{f.gw} BLANK</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="font-mono text-[10px] text-white/60 space-y-0.5">
                                                        <p className="font-mono text-[8px] uppercase tracking-widest text-white/40 mb-1.5">Komponenty modelu</p>
                                                        <p>Base PPG: <span className="text-white/85">{p.base_ppg}</span> (25/26: {p.ppg_25_26 ?? "—"} · historie: {p.hist_ppg ?? "—"} z {p.hist_games} zápasů)</p>
                                                        <p>Pravděpodobnost minut: <span className="text-white/85">{Math.round(p.minutes_prob * 100)}%</span></p>
                                                        <p>Odchylka ve fázi sezóny: <span className={p.phase_dev >= 0 ? "text-fa-mint" : "text-red-300"}>{p.phase_dev >= 0 ? "+" : ""}{p.phase_dev}</span></p>
                                                    </div>
                                                    <div className="font-mono text-[10px] text-white/60 space-y-0.5">
                                                        <p className="font-mono text-[8px] uppercase tracking-widest text-white/40 mb-1.5">Profil hráče</p>
                                                        <p>Konzistence: <span className="text-white/85">{p.consistency ?? "—"}</span> · Výbušnost: <span className="text-white/85">{p.explosiveness ?? "—"}</span></p>
                                                        <p>Haul rate: <span className="text-fa-yellow">{p.haul_rate != null ? `${p.haul_rate}%` : "—"}</span></p>
                                                        {p.preseason_share != null && <p className="text-fa-sky">Příprava: {p.preseason_share} % minut (#{p.preseason_rank} v týmu)</p>}
                                                        {p.new_coach && <p className="text-[#FF8800]">⚠ Nový trenér — jiný herní styl, jistota snížena</p>}
                                                        {p.is_radar && <p className="text-fa-sky">Nováček z Radaru · readiness {p.radar_readiness}</p>}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={11} className="px-3 py-6 text-center font-mono text-[11px] text-white/40">Žádný hráč neodpovídá filtrům</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <p className="font-mono text-[9px] text-white/35">
                    Predikce = base PPG (blend 25/26 + historie) × FDR profil hráče proti dané obtížnosti × fáze sezóny × pravděpodobnost minut, sečteno přes zápasy okna. Floor/strop odvozen z konzistence a výbušnosti. Komunita hlasuje ↑ = model podceňuje, ↓ = přeceňuje.
                </p>
            </div>
        </AppShell>
    );
}
